import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getLayerInfo } from "../../api/projects";
import { useSetFeatureClassification } from "../../hooks/useProjects";
import type { FeatureClassification, Layer } from "../../types/projects";
import {
  CANDIDATE_TYPE_COLORS,
  extractPreviewFeatures,
  featuresExtent,
  projectGeometry,
  type PreviewFeature,
} from "./featureGeometry";

type ProjectedGeometry = NonNullable<ReturnType<typeof projectGeometry>>;
import {
  ClassificationBadge,
  GeometryBadge,
} from "../components-step/ComponentBadge";
import { UserName } from "../common/UserName";
import { ACTION_LABELS, type FileAction } from "./fileRowState";

interface Props {
  projectId: string;
  /** ``null`` → render the empty-state placeholder (Stage 8.6 Item 3). */
  layer: Layer | null;
  primary: FileAction | null;
  /** Tooltip for disabled primary buttons (Stage 8.7: v1.1 tracker). */
  primaryDisabledReason?: string;
  /** Non-destructive secondary actions, in display order. */
  overflow: FileAction[];
  /** Destructive actions (``delete``) — surfaced below a thin rule. */
  destructive: FileAction[];
  /** Whether the parent is rendering this in collapse-below-table
   * mode (sub-1280px viewport). Only affects ``position: sticky``. */
  isCompact?: boolean;
  /** True while a background classifier is still running for this layer
   * (the Files tab is polling). Drives the "agent is classifying…" cue. */
  classifying?: boolean;
  onAction: (action: FileAction) => void;
}

/**
 * Stage 8.6 Files-tab side panel — persistent, NOT toggle-on-click.
 *
 * The panel is always rendered next to the file table. When no row
 * is selected the panel shows a Windows-Explorer-style empty state
 * (icon + "Select a single file to get more information"). When a
 * row is selected the panel shows preview thumbnail, metadata, and
 * the stacked action column.
 *
 * Below the 1280px viewport breakpoint the parent stacks the panel
 * below the table instead of side-by-side.
 */
export function FilePreviewPanel({
  projectId,
  layer,
  primary,
  primaryDisabledReason,
  overflow,
  destructive,
  isCompact = false,
  classifying = false,
  onAction,
}: Props) {
  if (!layer) {
    return <EmptyState testId="file-preview-empty" />;
  }
  return (
    <LoadedPanel
      projectId={projectId}
      layer={layer}
      classifying={classifying}
      primary={primary}
      primaryDisabledReason={primaryDisabledReason}
      overflow={overflow}
      destructive={destructive}
      isCompact={isCompact}
      onAction={onAction}
    />
  );
}

function EmptyState({ testId }: { testId: string }) {
  return (
    <aside data-testid={testId} style={emptyCardStyle}>
      <FileIcon />
      <p style={{ margin: 0, color: "#94a3b8", textAlign: "center" }}>
        Select a single file to get more information
      </p>
    </aside>
  );
}

function FileIcon() {
  return (
    <svg
      aria-hidden="true"
      width="56"
      height="64"
      viewBox="0 0 56 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", marginBottom: 14 }}
    >
      <path
        d="M8 4h28l12 12v44a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z"
        fill="#e2e8f0"
        stroke="#cbd5e1"
        strokeWidth="2"
      />
      <path d="M36 4v12h12" fill="#cbd5e1" />
    </svg>
  );
}

function LoadedPanel({
  projectId,
  layer,
  classifying,
  primary,
  primaryDisabledReason,
  overflow,
  destructive,
  isCompact,
  onAction,
}: {
  projectId: string;
  layer: Layer;
  classifying: boolean;
  primary: FileAction | null;
  primaryDisabledReason?: string;
  overflow: FileAction[];
  destructive: FileAction[];
  isCompact: boolean;
  onAction: (action: FileAction) => void;
}) {
  const filename = layer.original_filename || layer.name;
  const isPdf = filename.toLowerCase().endsWith(".pdf");

  const info = useQuery({
    queryKey: ["layer-info", projectId, layer.id],
    queryFn: () => getLayerInfo(projectId, layer.id),
    staleTime: 60_000,
    retry: 0,
  });

  return (
    <aside
      data-testid="file-preview-panel"
      style={{
        ...loadedCardStyle,
        position: isCompact ? "static" : "sticky",
        top: isCompact ? "auto" : "1rem",
        // Stage 8.7-followup: keep the panel within the visible viewport
        // so a long row of files doesn't push the preview below the
        // fold. The card itself scrolls internally when its content
        // exceeds the remaining height. ``1rem`` matches the ``top``
        // offset above so the panel never bleeds off the bottom edge.
        maxHeight: isCompact ? "none" : "calc(100vh - 2rem)",
        overflowY: isCompact ? "visible" : "auto",
      }}
    >
      <header style={{ marginBottom: "0.75rem" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.95rem",
            wordBreak: "break-word",
          }}
          data-testid="file-preview-name"
        >
          {filename}
        </div>
        <div style={{ marginTop: 4 }}>
          {isPdf ? (
            <ClassificationBadge
              classification={layer.classification}
              confidence={layer.classification_confidence}
            />
          ) : (
            <GeometryBadge layerType={layer.layer_type} />
          )}
        </div>
      </header>

      <dl style={metaGrid}>
        <dt>Uploaded by</dt>
        <dd>
          <UserName
            user={layer.uploaded_by}
            testId="file-preview-uploaded-by"
          />
        </dd>
        <dt>Uploaded</dt>
        <dd>{new Date(layer.created_at).toLocaleString()}</dd>
        <dt>Type</dt>
        <dd data-testid="file-preview-type">{humanFormatLabel(filename)}</dd>
        <dt>Size</dt>
        <dd data-testid="file-preview-size">
          {info.data?.size_bytes != null
            ? formatBytes(info.data.size_bytes)
            : info.isLoading
              ? "…"
              : "—"}
        </dd>
        {layer.feature_count > 0 && (
          <>
            <dt>Features</dt>
            <dd>{layer.feature_count}</dd>
          </>
        )}
      </dl>

      <FeatureClassificationsSection
        projectId={projectId}
        layer={layer}
        classifying={classifying}
      />


      <div data-testid="file-preview-actions" style={actionStackStyle}>
        {primary && (
          <button
            type="button"
            data-testid={`file-preview-action-${primary}`}
            onClick={() => onAction(primary)}
            disabled={primaryDisabledReason !== undefined}
            title={primaryDisabledReason}
            aria-disabled={primaryDisabledReason !== undefined}
            style={
              primaryDisabledReason !== undefined ? primaryBtnDisabled : primaryBtn
            }
          >
            {ACTION_LABELS[primary]}
          </button>
        )}
        {overflow.map((action) => (
          <button
            key={action}
            type="button"
            data-testid={`file-preview-action-${action}`}
            onClick={() => onAction(action)}
            style={secondaryBtn}
          >
            {ACTION_LABELS[action]}
          </button>
        ))}
        {!primary && overflow.length === 0 && destructive.length === 0 && (
          <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>
            No actions available yet.
          </div>
        )}
        {destructive.length > 0 && (
          <>
            <div
              aria-hidden="true"
              style={{
                borderTop: "1px solid #f3f4f6",
                margin: "0.5rem 0 0.25rem",
              }}
            />
            {destructive.map((action) => (
              <button
                key={action}
                type="button"
                data-testid={`file-preview-action-${action}`}
                onClick={() => onAction(action)}
                style={destructiveBtn}
              >
                {ACTION_LABELS[action]}
              </button>
            ))}
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * Stage 8.8 — per-feature classification list.
 *
 * Renders below the metadata grid when the layer is a geometry file
 * with multiple features. Three states:
 *
 * - **Pending** (geometry file, classifier hasn't run yet) → shows a
 *   "Classifying N features…" spinner-style line.
 * - **Ready** (classifications present) → one row per feature with a
 *   colored type chip + hoverable agent reasoning.
 * - **N/A** (PDF, drawn, single-feature geometry) → nothing rendered.
 */
function FeatureClassificationsSection({
  projectId,
  layer,
  classifying,
}: {
  projectId: string;
  layer: Layer;
  classifying: boolean;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const features = useMemo(() => extractPreviewFeatures(layer), [layer]);
  // Map feature_id → display name so the rows can show "Wetland A"
  // instead of the opaque "feature_1".
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of features) m.set(f.featureId, f.name);
    return m;
  }, [features]);

  const filename = (layer.original_filename || layer.name || "").toLowerCase();
  const isGeometry =
    filename.endsWith(".kmz") ||
    filename.endsWith(".kml") ||
    filename.endsWith(".geojson") ||
    filename.endsWith(".json");
  if (!isGeometry || layer.feature_count <= 1) return null;

  // Classifications are an OPTIONAL overlay. The feature list comes from
  // the geometry itself (inline geojson) so features are selectable even
  // when the agent classifier never ran — e.g. no Anthropic key. Fall
  // back to classification keys only if the geometry is stored in S3 (no
  // inline geojson) but suggestions happen to exist.
  const classifications = layer.feature_classifications ?? {};
  const hasClassifications = Object.keys(classifications).length > 0;
  const rowIds =
    features.length > 0
      ? features.map((f) => f.featureId)
      : Object.keys(classifications);

  if (rowIds.length === 0) {
    return (
      <div
        data-testid="feature-classifications-pending"
        style={pendingClassifyStyle}
      >
        Preparing {layer.feature_count} features…
      </div>
    );
  }

  return (
    <section
      data-testid="feature-classifications-list"
      style={featureListStyle}
    >
      <h4 style={featureListHeading}>Feature classifications</h4>
      {classifying && (
        <div data-testid="feature-classifying-banner" style={classifyingStyle}>
          <span style={spinnerStyle} aria-hidden />
          Agent is classifying these features…
        </div>
      )}
      <FeatureGeometryPreview
        features={features}
        classifications={classifications}
        bounds={layer.bounds}
        hoveredId={hoveredId}
        onHover={setHoveredId}
      />
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {rowIds.map((featureId) => (
          <FeatureClassificationRow
            key={featureId}
            projectId={projectId}
            layerId={layer.id}
            featureId={featureId}
            featureName={nameById.get(featureId) ?? featureId}
            classification={classifications[featureId] ?? null}
            isHovered={hoveredId === featureId}
            onHover={setHoveredId}
          />
        ))}
      </ul>
      <p style={featureListHint}>
        {hasClassifications
          ? "Hover a feature to highlight it on the map. Each shows the agent's suggestion — override it with the dropdown. "
          : "Set each feature's type with the dropdown — boundary (build area), exclusion / road (no-build), or poi. Hover to highlight it on the map. "}
        "Add to site plan candidates" routes features using the values
        shown here.
      </p>
    </section>
  );
}

/**
 * Inline SVG map of every feature in the layer, projected from WGS84
 * lon/lat into the box. Each feature is colored by its classification;
 * hovering a feature (here or in the list) thickens its stroke and dims
 * the rest so the operator can tell what "feature_0" is on the ground.
 */
function FeatureGeometryPreview({
  features,
  classifications,
  bounds,
  hoveredId,
  onHover,
}: {
  features: PreviewFeature[];
  classifications: Record<string, FeatureClassification>;
  bounds: Layer["bounds"];
  hoveredId: string | null;
  onHover: (id: string | null) => void;
}) {
  const W = 240;
  const H = 160;
  const extent = useMemo(
    () => featuresExtent(features, bounds),
    [features, bounds],
  );
  const drawable = useMemo(
    () =>
      features
        .map((f) => ({
          feature: f,
          projected:
            f.geometry && extent
              ? projectGeometry(f.geometry, extent, W, H)
              : null,
        }))
        .filter(
          (d): d is { feature: PreviewFeature; projected: ProjectedGeometry } =>
            d.projected !== null,
        ),
    [features, extent],
  );

  if (!extent || drawable.length === 0) return null;

  return (
    <svg
      data-testid="feature-geometry-preview"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="Feature geometry preview"
      style={featurePreviewSvgStyle}
    >
      {drawable.map(({ feature, projected }) => {
        const fc = classifications[feature.featureId];
        const color = fc
          ? CANDIDATE_TYPE_COLORS[fc.suggested_type]
          : "#94a3b8";
        const isHovered = hoveredId === feature.featureId;
        const dim = hoveredId !== null && !isHovered;
        const common = {
          onMouseEnter: () => onHover(feature.featureId),
          onMouseLeave: () => onHover(null),
          opacity: dim ? 0.25 : 1,
          style: { cursor: "pointer" as const },
        };
        if (projected.kind === "point") {
          const [x, y] = projected.d.split(",").map(Number);
          return (
            <circle
              key={feature.featureId}
              cx={x}
              cy={y}
              r={isHovered ? 6 : 4}
              fill={color}
              stroke="#fff"
              strokeWidth={1}
              {...common}
            />
          );
        }
        if (projected.kind === "polyline") {
          return (
            <polyline
              key={feature.featureId}
              points={projected.d}
              fill="none"
              stroke={color}
              strokeWidth={isHovered ? 3.5 : 2}
              {...common}
            />
          );
        }
        return (
          <path
            key={feature.featureId}
            d={projected.d}
            fill={color}
            fillOpacity={isHovered ? 0.45 : 0.2}
            stroke={color}
            strokeWidth={isHovered ? 2.5 : 1.25}
            {...common}
          />
        );
      })}
    </svg>
  );
}

const FEATURE_TYPE_OPTIONS: FeatureClassification["suggested_type"][] = [
  "boundary",
  "exclusion",
  "road",
  "poi",
];

function FeatureClassificationRow({
  projectId,
  layerId,
  featureId,
  featureName,
  classification,
  isHovered,
  onHover,
}: {
  projectId: string;
  layerId: string;
  featureId: string;
  featureName: string;
  /** ``null`` when neither the agent nor the user has classified this
   * feature yet — the dropdown shows a placeholder and the dot is grey. */
  classification: FeatureClassification | null;
  isHovered: boolean;
  onHover: (id: string | null) => void;
}) {
  const setClassification = useSetFeatureClassification(projectId);
  const chipStyle = classification
    ? SUGGESTED_TYPE_STYLES[classification.suggested_type]
    : unsetChipStyle;
  const confidence = classification
    ? Math.round(classification.confidence * 100)
    : 0;
  const isUser = classification?.source === "user";
  const tooltip = !classification
    ? "Not set — pick a type"
    : isUser
      ? "Set manually by you"
      : classification.reasoning
        ? `Suggested by agent (${confidence}%) — ${classification.reasoning}`
        : `Suggested by agent (${confidence}%)`;
  // Show the name; only repeat the raw feature_id when it differs (i.e.
  // the feature actually had a name) so we don't render "feature_0 · feature_0".
  const showRawId = featureName !== featureId;

  return (
    <li
      data-testid={`feature-classification-row-${featureId}`}
      onMouseEnter={() => onHover(featureId)}
      onMouseLeave={() => onHover(null)}
      style={{
        ...featureRowStyle,
        background: isHovered ? "#eef2ff" : "transparent",
        borderRadius: 6,
      }}
    >
      <span
        style={{
          fontSize: "0.85rem",
          color: "#334155",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={showRawId ? `${featureName} (${featureId})` : featureName}
      >
        {featureName}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span
          aria-hidden
          data-testid={`feature-chip-${featureId}`}
          title={tooltip}
          style={{ ...chipDotStyle, ...chipStyle }}
        />
        <select
          aria-label={`Classification for ${featureId}`}
          title={tooltip}
          value={classification?.suggested_type ?? ""}
          disabled={setClassification.isPending}
          onChange={(e) =>
            setClassification.mutate({
              layerId,
              featureId,
              suggestedType: e.target
                .value as FeatureClassification["suggested_type"],
            })
          }
          style={featureSelectStyle}
        >
          {classification === null && (
            <option value="" disabled>
              — set type —
            </option>
          )}
          {FEATURE_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </span>
    </li>
  );
}

const SUGGESTED_TYPE_STYLES: Record<
  FeatureClassification["suggested_type"],
  React.CSSProperties
> = {
  boundary: { background: "#ddd6fe", color: "#5b21b6" },
  exclusion: { background: "#fee2e2", color: "#b91c1c" },
  road: { background: "#fef3c7", color: "#92400e" },
  poi: { background: "#dbeafe", color: "#1d4ed8" },
};

const unsetChipStyle: React.CSSProperties = {
  background: "#cbd5e1",
  color: "#475569",
};

const chipDotStyle: React.CSSProperties = {
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: 999,
  flexShrink: 0,
  cursor: "help",
};

const featureSelectStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "2px 6px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#334155",
  cursor: "pointer",
};

const pendingClassifyStyle: React.CSSProperties = {
  marginTop: "1rem",
  padding: "0.6rem 0.75rem",
  background: "#f8fafc",
  border: "1px dashed #cbd5e1",
  borderRadius: 8,
  color: "#64748b",
  fontSize: "0.85rem",
};

const featureListStyle: React.CSSProperties = {
  marginTop: "1rem",
  padding: "0.75rem",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
};

const classifyingStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: "0.6rem",
  padding: "0.4rem 0.6rem",
  background: "#eef2ff",
  border: "1px solid #c7d2fe",
  borderRadius: 6,
  color: "#4338ca",
  fontSize: "0.8rem",
  fontWeight: 500,
};

const spinnerStyle: React.CSSProperties = {
  display: "inline-block",
  width: 12,
  height: 12,
  flexShrink: 0,
  border: "2px solid #c7d2fe",
  borderTopColor: "#4338ca",
  borderRadius: "50%",
  animation: "spark-spin 0.8s linear infinite",
};

const featurePreviewSvgStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  height: "auto",
  marginBottom: "0.6rem",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
};

const featureListHeading: React.CSSProperties = {
  margin: "0 0 0.5rem",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#64748b",
};

const featureRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  padding: "0.3rem 0",
  borderBottom: "1px solid #e2e8f0",
};

const featureListHint: React.CSSProperties = {
  margin: "0.5rem 0 0",
  fontSize: "0.75rem",
  color: "#94a3b8",
};

function humanFormatLabel(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".pdf")) return "PDF";
  if (lower.endsWith(".kmz")) return "KMZ";
  if (lower.endsWith(".kml")) return "KML";
  if (lower.endsWith(".geojson") || lower.endsWith(".json")) return "GeoJSON";
  if (lower.endsWith(".dwg")) return "DWG";
  if (lower.endsWith(".dxf")) return "DXF";
  return "File";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const emptyCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px dashed #e2e8f0",
  borderRadius: 10,
  padding: "2rem 1.25rem",
  minHeight: 240,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

const loadedCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "1.25rem",
};

const metaGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gap: "0.35rem 0.75rem",
  margin: "1rem 0 0",
  fontSize: "0.85rem",
};

const actionStackStyle: React.CSSProperties = {
  marginTop: "1rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

const primaryBtn: React.CSSProperties = {
  background: "#635bff",
  color: "#ffffff",
  border: "none",
  padding: "0.6rem 0.9rem",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
};

const primaryBtnDisabled: React.CSSProperties = {
  ...primaryBtn,
  background: "#cbd5e1",
  color: "#475569",
  cursor: "not-allowed",
};

const secondaryBtn: React.CSSProperties = {
  background: "#ffffff",
  color: "#0a0e27",
  border: "1px solid #d1d5db",
  padding: "0.55rem 0.9rem",
  borderRadius: 6,
  cursor: "pointer",
};

const destructiveBtn: React.CSSProperties = {
  background: "transparent",
  color: "#b91c1c",
  border: "1px solid transparent",
  padding: "0.5rem 0.9rem",
  borderRadius: 6,
  cursor: "pointer",
  textAlign: "left",
  fontSize: "0.9rem",
};
